import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarClock, Search, CheckSquare, Square, Users, Clock, Send, Sparkles,
  RefreshCw, MessageSquare, ChevronRight, AlertCircle, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function SMSStatusPill({ status }) {
  const map = {
    not_sent:  { label: "Not sent",  cls: "bg-slate-100 text-slate-600" },
    sent:      { label: "Sent",      cls: "bg-sky-100 text-sky-700" },
    delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-700" },
    replied:   { label: "Replied",   cls: "bg-violet-100 text-violet-700" },
    error:     { label: "Failed",    cls: "bg-red-100 text-red-700" },
  };
  const m = map[status] || map.not_sent;
  return <Badge className={`${m.cls} border-0 text-[10px] uppercase tracking-widest`} data-testid={`sms-status-${status}`}>{m.label}</Badge>;
}

export default function AdminBulkScheduler() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({}); // {clientId: true}
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayISO());
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("14:00");
  const [duration, setDuration] = useState(60);
  const [tz] = useState(DEFAULT_TZ);
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);

  const [batch, setBatch] = useState(null); // {batch_id, appointments, counts}
  const [previewDialog, setPreviewDialog] = useState({ open: false, appt: null, text: "", sending: false });

  const loadClients = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bulk-schedule/clients", { params: q ? { q } : {} });
      setClients(data.clients || []);
    } catch (e) {
      toast.error("Couldn't load Close-synced clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClients(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(loadClients, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectAllVisible = () => {
    const next = { ...selected };
    clients.forEach((c) => { next[c.id] = true; });
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  const runPreview = async () => {
    if (selectedIds.length === 0) return toast.error("Pick at least one client.");
    try {
      const { data } = await api.post("/bulk-schedule/preview", {
        date, start_time: startTime, end_time: endTime,
        duration_minutes: duration, client_ids: selectedIds, timezone: tz,
      });
      setPreview(data);
      setBatch(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    }
  };

  const commitBatch = async () => {
    if (!preview) return runPreview();
    setCreating(true);
    try {
      const { data } = await api.post("/bulk-schedule/create-batch", {
        date, start_time: startTime, end_time: endTime,
        duration_minutes: duration, client_ids: selectedIds, timezone: tz,
      });
      setBatch({ batch_id: data.batch_id, appointments: data.appointments });
      setPreview(null);
      setSelected({});
      toast.success(`Booked ${data.appointments.length} appointment(s). Nothing was messaged yet.`);
      refreshBatch(data.batch_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create batch.");
    } finally {
      setCreating(false);
    }
  };

  const refreshBatch = async (bid) => {
    const id = bid || batch?.batch_id;
    if (!id) return;
    try {
      const { data } = await api.get(`/bulk-schedule/batches/${id}`);
      setBatch(data);
    } catch (e) { /* silent */ }
  };

  const openSMSPreview = async (appt) => {
    try {
      const { data } = await api.get(`/bulk-schedule/appointments/${appt.id}/sms-preview`);
      setPreviewDialog({ open: true, appt, text: data.preview, sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load preview.");
    }
  };

  const sendSMS = async () => {
    const { appt, text } = previewDialog;
    if (!appt) return;
    setPreviewDialog((p) => ({ ...p, sending: true }));
    try {
      await api.post(`/bulk-schedule/appointments/${appt.id}/send-sms`, { text });
      toast.success(`SMS sent to ${appt.client_name || appt.client_phone}.`);
      setPreviewDialog({ open: false, appt: null, text: "", sending: false });
      refreshBatch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS failed.");
      setPreviewDialog((p) => ({ ...p, sending: false }));
    }
  };

  const sendAll = async () => {
    if (!batch?.batch_id) return;
    if (!window.confirm(`Send confirmation SMS to every unsent appointment in this batch?`)) return;
    try {
      const { data } = await api.post(`/bulk-schedule/batches/${batch.batch_id}/send-all-sms`, {});
      toast.success(`Sent ${data.sent} · Failed ${data.failed}`);
      refreshBatch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed.");
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="bulk-scheduler-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-slate-400">Operations</p>
            <h1 className="text-3xl font-display font-black text-slate-900 flex items-center gap-2">
              <CalendarClock className="text-emerald-500" /> Bulk Appointment Scheduler
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Tick clients from Close, pick a day + time window, and we'll auto-space slots and round-robin them across the team.
              Nothing gets messaged until you click send.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadClients} data-testid="refresh-clients">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
          {/* Left: client picker */}
          <Card className="p-4 rounded-2xl border-slate-100 flex flex-col min-h-[520px]" data-testid="client-picker">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} className="text-slate-500" />
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Close-synced clients</p>
              <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-0" data-testid="selected-count">
                {selectedIds.length} selected
              </Badge>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, phone"
                className="pl-8 h-9 text-sm"
                data-testid="client-search"
              />
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <button className="text-emerald-600 hover:underline" onClick={selectAllVisible} data-testid="select-all">Select all shown</button>
              <span className="text-slate-300">·</span>
              <button className="text-slate-500 hover:underline" onClick={clearSelection} data-testid="clear-selection">Clear</button>
            </div>
            <div className="flex-1 overflow-y-auto mt-3 -mx-2 px-2 space-y-1">
              {loading && (
                <p className="text-sm text-slate-400 text-center py-6"><Loader2 className="inline animate-spin mr-1" size={14} /> Loading…</p>
              )}
              {!loading && clients.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">No Close-synced clients found.</p>
              )}
              {clients.map((c) => {
                const checked = !!selected[c.id];
                return (
                  <label
                    key={c.id}
                    className={`w-full flex items-start gap-2 rounded-lg p-2 cursor-pointer transition border ${checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-100 hover:bg-slate-50"}`}
                    data-testid={`client-row-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-emerald-600"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      data-testid={`client-check-${c.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{c.phone || "no phone"} · {c.status || "—"}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>

          {/* Right: schedule config + preview + booked table */}
          <div className="space-y-4">
            <Card className="p-5 rounded-2xl border-slate-100" data-testid="schedule-config">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                <Clock size={14} /> Slot window
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="input-date" />
                </div>
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="input-start" />
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="input-end" />
                </div>
                <div>
                  <Label className="text-xs">Per-call (min)</Label>
                  <Input type="number" min={15} max={240} step={15} value={duration} onChange={(e) => setDuration(parseInt(e.target.value || "60", 10))} data-testid="input-duration" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button onClick={runPreview} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-preview">
                  <Sparkles size={14} className="mr-1" /> Preview slots ({selectedIds.length})
                </Button>
                <Button
                  onClick={commitBatch}
                  disabled={!preview || creating}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="btn-commit"
                >
                  {creating ? <><Loader2 className="animate-spin mr-1" size={14} /> Booking…</> : <><ChevronRight size={14} className="mr-1" /> Confirm & Book</>}
                </Button>
                <span className="text-xs text-slate-400">Timezone: {tz}</span>
              </div>
            </Card>

            {/* Preview slots */}
            {preview && (
              <Card className="p-5 rounded-2xl border-emerald-200 bg-emerald-50/40" data-testid="preview-slots">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={14} className="text-emerald-600" />
                  <p className="text-xs uppercase tracking-widest font-bold text-emerald-800">Preview — nothing booked yet</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="py-2 pr-3">Time</th>
                        <th className="py-2 pr-3">Client</th>
                        <th className="py-2 pr-3">Phone</th>
                        <th className="py-2 pr-3">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.slots.map((s, i) => (
                        <tr key={i} className="border-t border-emerald-100/70">
                          <td className="py-2 pr-3 font-mono text-slate-800">{s.local_time_label} <span className="text-slate-400">{s.tz_short}</span></td>
                          <td className="py-2 pr-3">{s.client_name}</td>
                          <td className="py-2 pr-3 text-slate-500">{s.client_phone || "—"}</td>
                          <td className="py-2 pr-3">
                            <Badge className="bg-slate-900 text-white border-0">{s.admin_owner_name || "—"}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Booked appointments */}
            {batch && (
              <Card className="p-5 rounded-2xl border-slate-100" data-testid="booked-table">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <MessageSquare size={16} className="text-emerald-600" />
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Booked appointments · Batch {String(batch.batch_id).slice(0, 8)}</p>
                  <Button size="sm" variant="outline" className="ml-auto" onClick={() => refreshBatch()} data-testid="btn-batch-refresh">
                    <RefreshCw size={12} className="mr-1" /> Refresh
                  </Button>
                  <Button size="sm" onClick={sendAll} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-send-all">
                    <Send size={12} className="mr-1" /> Send all SMS
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="py-2 pr-3">Time</th>
                        <th className="py-2 pr-3">Client</th>
                        <th className="py-2 pr-3">Admin</th>
                        <th className="py-2 pr-3">SMS</th>
                        <th className="py-2 pr-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(batch.appointments || []).map((a) => (
                        <tr key={a.id} className="border-t border-slate-100" data-testid={`appt-row-${a.id}`}>
                          <td className="py-2 pr-3 font-mono text-slate-800">{a.local_time_label} <span className="text-slate-400">{a.tz_short}</span></td>
                          <td className="py-2 pr-3">
                            <div className="font-semibold text-slate-900">{a.client_name}</div>
                            <div className="text-[11px] text-slate-500 font-mono">{a.client_phone || "no phone"}</div>
                          </td>
                          <td className="py-2 pr-3"><Badge className="bg-slate-900 text-white border-0">{a.admin_owner_name || "—"}</Badge></td>
                          <td className="py-2 pr-3">
                            <SMSStatusPill status={a.sms_status || "not_sent"} />
                            {a.sms_last_error && (
                              <div className="text-[10px] text-red-600 flex items-center gap-1 mt-1"><AlertCircle size={10} /> {a.sms_last_error}</div>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <Button size="sm" variant="outline" onClick={() => openSMSPreview(a)} data-testid={`btn-send-${a.id}`}>
                              <Send size={12} className="mr-1" /> Preview & Send
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* SMS Preview dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(o) => !o && setPreviewDialog({ open: false, appt: null, text: "", sending: false })}>
        <DialogContent data-testid="sms-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview SMS</DialogTitle>
            <DialogDescription>
              Sending to <b>{previewDialog.appt?.client_name}</b> · <span className="font-mono">{previewDialog.appt?.client_phone}</span>
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={previewDialog.text}
            onChange={(e) => setPreviewDialog((p) => ({ ...p, text: e.target.value }))}
            rows={6}
            data-testid="sms-preview-text"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog({ open: false, appt: null, text: "", sending: false })}>Cancel</Button>
            <Button onClick={sendSMS} disabled={previewDialog.sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-send-sms">
              {previewDialog.sending ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
              Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
